import React, { useState } from 'react';
import { AlertTriangle, Check, Mail, X } from 'lucide-react';
import { REMINDER_LABELS, REMINDER_TYPES, formatRelativeTime } from '../../lib/reminders';

/**
 * Confirms a batch of reminder emails before they are queued.
 *
 * Worth a dialog rather than a bare button: the Pending Self-Review stage
 * routinely holds 150+ people, so a stray click is 150 emails. The dialog states
 * the count, names anyone who will be skipped for want of an address, and warns
 * when somebody was already reminded in the last 24 hours — the re-send is still
 * allowed, because a deliberate second nudge is a real need, but it is never
 * silent.
 *
 * Errors stay in here rather than bubbling to App's full-screen permission page,
 * matching the employee modals.
 */
export default function SendReminderModal({
  isOpen, onClose, onConfirm, type, recipientCount, skipped = [], recentlyReminded = [], periodLabel,
}) {
  const [isBusy, setIsBusy] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  if (!isOpen) return null;

  const isManagerReminder = type === REMINDER_TYPES.MANAGER_REVIEW;
  const label = REMINDER_LABELS[type] || 'Reminder';

  const close = () => {
    setError('');
    setResult(null);
    onClose();
  };

  const handleConfirm = async () => {
    setIsBusy(true);
    setError('');
    try {
      const outcome = await onConfirm();
      if (outcome?.ok) setResult(outcome);
      else setError(outcome?.message || 'The reminders could not be queued.');
    } catch (unexpected) {
      console.error('Queueing reminders failed:', unexpected);
      setError('The reminders could not be queued. Please try again.');
    } finally {
      setIsBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-blue-100 flex items-center justify-center">
              <Mail size={18} className="text-blue-700" />
            </div>
            <div>
              <h2 className="text-base font-bold text-gray-900">{label}</h2>
              <p className="text-[11px] text-gray-500">{periodLabel}</p>
            </div>
          </div>
          <button type="button" onClick={close} className="text-gray-400 hover:text-gray-600 p-1">
            <X size={20} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {result ? (
            <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs rounded-lg px-3 py-2.5">
              <p className="flex items-start gap-1.5">
                <Check size={13} className="mt-0.5 flex-shrink-0" />
                <span>
                  <span className="font-semibold">{result.queued}</span>{' '}
                  {result.queued === 1 ? 'reminder' : 'reminders'} queued. They are sent from
                  hr@ruskmedia.com by the APEX workflow, normally within a few minutes.
                </span>
              </p>
            </div>
          ) : (
            <>
              <p className="text-sm text-gray-700">
                {recipientCount === 0 ? (
                  'There is nobody to remind in this stage.'
                ) : isManagerReminder ? (
                  <>
                    <span className="font-semibold">{recipientCount}</span>{' '}
                    {recipientCount === 1 ? 'email' : 'emails'} will be queued — one per reportee
                    awaiting feedback, each naming that reportee. A manager with several reportees
                    receives one email per person.
                  </>
                ) : (
                  <>
                    <span className="font-semibold">{recipientCount}</span>{' '}
                    {recipientCount === 1 ? 'employee' : 'employees'} will be asked to complete
                    their self-evaluation.
                  </>
                )}
              </p>

              <p className="text-[11px] text-gray-500">
                Nothing is sent from this app. Each reminder is written to Firestore and the APEX
                n8n workflow sends it from hr@ruskmedia.com.
              </p>

              {recentlyReminded.length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2.5">
                  <p className="flex items-start gap-1.5 text-[11px] text-amber-800">
                    <AlertTriangle size={12} className="mt-0.5 flex-shrink-0 text-amber-600" />
                    <span>
                      <span className="font-semibold">
                        {recentlyReminded.length} {recentlyReminded.length === 1 ? 'person was' : 'people were'} already
                        reminded in the last 24 hours
                      </span>{' '}
                      and will be emailed again:{' '}
                      {recentlyReminded.slice(0, 4).map(entry => (
                        `${entry.name}${entry.lastQueuedAt ? ` (${formatRelativeTime(entry.lastQueuedAt)})` : ''}`
                      )).join(', ')}
                      {recentlyReminded.length > 4 && `, +${recentlyReminded.length - 4} more`}.
                    </span>
                  </p>
                </div>
              )}

              {skipped.length > 0 && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5">
                  <p className="text-[11px] font-semibold text-gray-700 mb-1">
                    {skipped.length} {skipped.length === 1 ? 'person' : 'people'} will be skipped
                  </p>
                  <ul className="space-y-0.5">
                    {skipped.slice(0, 6).map((entry, index) => (
                      <li key={`${entry.name}-${index}`} className="text-[11px] text-gray-600">
                        {entry.name} — {entry.reason}
                      </li>
                    ))}
                  </ul>
                  {skipped.length > 6 && (
                    <p className="text-[11px] text-gray-500 mt-0.5">+{skipped.length - 6} more</p>
                  )}
                  {isManagerReminder && (
                    <p className="text-[11px] text-gray-500 mt-1.5">
                      Set a manager on the Employee Management tab to include them next time.
                    </p>
                  )}
                </div>
              )}

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 text-xs rounded-lg px-3 py-2">
                  {error}
                </div>
              )}
            </>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 px-5 pb-5">
          <button
            type="button"
            onClick={close}
            disabled={isBusy}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50"
          >
            {result ? 'Done' : 'Cancel'}
          </button>
          {!result && (
            <button
              type="button"
              onClick={handleConfirm}
              disabled={isBusy || recipientCount === 0}
              className="px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isBusy ? 'Queueing…' : `Send ${recipientCount || ''}`.trim()}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
